import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ordersApi, type Order, type OrderStatus } from "../api/orders";
import { useAuth } from "../auth/AuthContext";
import { OrderStatusBadge } from "./OrderStatusBadge";

/**
 * Role-aware pending-orders widget.
 * • SALES / ADMIN see SUBMITTED orders that need triage.
 * • Customers (CUST_PROCUREMENT/CUST_MANAGER) see their own DRAFT orders
 * so they're nudged to finish them.
 */
export function PendingOrdersWidget({
  limit = 5,
  companyId,
}: {
  limit?: number;
  /** When set, scope the list to that company (Sales "Customer view" preview). */
  companyId?: string;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [items, setItems] = useState<Order[] | null>(null);

  useEffect(() => {
    const status: OrderStatus = hasPermission("orders:transition") ? "SUBMITTED" : "DRAFT";
    // Vidžet pokazuje nekoliko redova, pa toliko i traži: sečenje posle učitavanja
    // znači da je ceo spisak ipak prešao mrežu.
    void ordersApi
      .list({ status, companyId, size: limit })
      .then((r) => setItems(r.content))
      .catch(() => setItems([]));
  }, [hasPermission, limit, companyId]);

  const isSales = hasPermission("orders:transition");
  const title = isSales
    ? t("widgets.pendingOrders.titleSales")
    : t("widgets.pendingOrders.titleCustomer");
  const allLink = isSales ? "/admin/orders" : "/orders";

  if (items === null) return null;
  return (
    <section style={card}>
      <header style={head}>
        <strong>{title}</strong>
        <Link to={allLink} className="widget-more-link" style={moreLink}>{t("widgets.pendingOrders.viewAll")}</Link>
      </header>
      {items.length === 0 ? (
        <p style={empty}>
          {isSales
            ? t("widgets.pendingOrders.emptySales")
            : t("widgets.pendingOrders.emptyCustomer")}
        </p>
      ) : (
        <ul style={list}>
          {items.map((o) => (
            <li key={o.id}>
              <Link to={isSales ? `/admin/orders` : `/orders/${o.id}`} style={itemLink}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <code style={{ fontSize: 12, color: "var(--brand-ink)" }}>{o.orderNumber}</code>
                  <OrderStatusBadge status={o.status} />
                </div>
                <small style={{ display: "block", color: "var(--ink-3)", marginTop: 2 }}>
                  {t("widgets.pendingOrders.linesSummary", {
                    company: o.companyName,
                    count: o.lines.length,
                  })}
                </small>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)", padding: 16, borderRadius: 10, boxShadow: "var(--shadow-1)",
  display: "flex", flexDirection: "column", gap: 12,
};
const head: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--ink)", fontSize: 14 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 };
const itemLink: React.CSSProperties = { textDecoration: "none", display: "block", padding: 8, borderRadius: 6 };
const moreLink: React.CSSProperties = { color: "var(--brand-ink)", fontSize: 12, textDecoration: "none" };
const empty: React.CSSProperties = { color: "var(--ink-4)", fontSize: 13, margin: 0 };
